import React, { useState } from 'react';
import {
  useStripe,
  useElements,
  CardElement,
  PaymentElement,
} from '@stripe/react-stripe-js';
import { usePaymentsContext } from '../../shared/providers/PaymentsProvider';
import { PaymentIntent } from '@stripe/stripe-js';
import { ERROR_MESSAGES } from '../../shared/constants';
import { validateEmail } from '../../shared/utils/validation';

export interface PaymentFormProps {
  clientSecret?: string;
  customerId?: string;
  customerEmail?: string;
  amount?: number;
  currency?: string;
  description?: string;
  metadata?: Record<string, string>;
  onSuccess?: (paymentIntent: PaymentIntent) => void;
  onError?: (error: string) => void;
  onLoading?: (loading: boolean) => void;
  className?: string;
  showBillingDetails?: boolean;
  elementType?: 'card' | 'payment';
}

export const PaymentForm: React.FC<PaymentFormProps> = ({
  clientSecret,
  customerId,
  customerEmail,
  amount,
  currency = 'usd',
  description,
  metadata,
  onSuccess,
  onError,
  onLoading,
  className = '',
  showBillingDetails = true,
  elementType = 'payment',
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { initialized } = usePaymentsContext();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(customerEmail || '');
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: customerEmail || '',
    phone: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
    },
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      const error = ERROR_MESSAGES.STRIPE_NOT_LOADED;
      onError?.(error);
      return;
    }

    if (!initialized) {
      const error = ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED;
      onError?.(error);
      return;
    }

    if (showBillingDetails && email && !validateEmail(email)) {
      onError?.('Please enter a valid email address');
      return;
    }

    setLoading(true);
    onLoading?.(true);

    try {
      let result;

      if (elementType === 'payment' && clientSecret) {
        // Use Payment Element with existing Payment Intent
        result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.href,
            receipt_email: email || billingDetails.email,
          },
          redirect: 'if_required',
        });
      } else if (elementType === 'card') {
        // Use Card Element
        const cardElement = elements.getElement(CardElement);

        if (!cardElement) {
          throw new Error('Card element not found');
        }

        if (clientSecret) {
          // Confirm existing Payment Intent
          result = await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: showBillingDetails
                ? {
                    name: billingDetails.name || undefined,
                    email: billingDetails.email || undefined,
                    phone: billingDetails.phone || undefined,
                    address: billingDetails.address.line1
                      ? billingDetails.address
                      : undefined,
                  }
                : undefined,
            },
          });
        } else if (amount) {
          // Create new Payment Intent
          const response = await fetch('/api/payments/create-payment-intent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount,
              currency,
              customerId,
              description,
              metadata,
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to create payment intent');
          }

          const { client_secret } = await response.json();

          result = await stripe.confirmCardPayment(client_secret, {
            payment_method: {
              card: cardElement,
              billing_details: showBillingDetails
                ? {
                    name: billingDetails.name || undefined,
                    email: billingDetails.email || undefined,
                    phone: billingDetails.phone || undefined,
                    address: billingDetails.address.line1
                      ? billingDetails.address
                      : undefined,
                  }
                : undefined,
            },
          });
        } else {
          throw new Error('Either clientSecret or amount is required');
        }
      }

      if (result?.error) {
        throw new Error(result.error.message || ERROR_MESSAGES.PAYMENT_FAILED);
      }

      if (result?.paymentIntent?.status === 'succeeded') {
        onSuccess?.(result.paymentIntent);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : ERROR_MESSAGES.PAYMENT_FAILED;
      onError?.(errorMessage);
      console.error('Payment error:', error);
    } finally {
      setLoading(false);
      onLoading?.(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className={`payment-form ${className}`}>
      {showBillingDetails && (
        <div className="mb-6 space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setBillingDetails(prev => ({ ...prev, email: e.target.value }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="your@email.com"
              required
            />
          </div>

          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Full Name
            </label>
            <input
              type="text"
              id="name"
              value={billingDetails.name}
              onChange={e =>
                setBillingDetails(prev => ({ ...prev, name: e.target.value }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="John Doe"
            />
          </div>
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Information
        </label>
        <div className="p-3 border border-gray-300 rounded-md">
          {elementType === 'payment' ? (
            <PaymentElement />
          ) : (
            <CardElement options={cardElementOptions} />
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
      >
        {loading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Processing...
          </>
        ) : (
          `Pay ${amount ? `$${(amount / 100).toFixed(2)}` : ''}`
        )}
      </button>
    </form>
  );
};

export default PaymentForm;
